import React, { useState, useEffect } from "react";
import { listSocialPosts, publishSocialPost, deleteSocialPost } from "../../../api";
import { useToast } from "../../../hooks/use-toast";
import PostComposer from "./PostComposer";
import AdModal from "./AdModal";
import { Plus, Facebook, Instagram, Send, Rocket, Pencil, Trash2, Heart, MessageCircle, Share2, Eye, ShieldCheck } from "lucide-react";

const imgSrc = (u) => (u?.startsWith("http") ? u : `${process.env.REACT_APP_BACKEND_URL}${u}`);

const STATUS = {
  draft: { l: "Draft", c: "bg-gray-200 text-gray-600" },
  scheduled: { l: "Scheduled", c: "bg-yellow-100 text-yellow-800" },
  published: { l: "Published", c: "bg-green-100 text-green-800" },
};

const SocialPosts = ({ onChange }) => {
  const [posts, setPosts] = useState([]);
  const [composer, setComposer] = useState(false);
  const [editPost, setEditPost] = useState(null);
  const [boostPost, setBoostPost] = useState(null);
  const { toast } = useToast();

  const load = () => listSocialPosts().then(setPosts).catch(() => setPosts([]));
  useEffect(() => { load(); }, []);

  const refresh = () => { load(); onChange?.(); };

  const publish = async (id) => { try { await publishSocialPost(id); toast({ title: "Published (demo)" }); refresh(); } catch { toast({ title: "Could not publish", variant: "destructive" }); } };
  const remove = async (id) => { if (!window.confirm("Delete this post? Any linked ad will also be removed.")) return; try { await deleteSocialPost(id); refresh(); } catch { toast({ title: "Could not delete", variant: "destructive" }); } };

  return (
    <div data-testid="social-posts">
      <div className="flex items-center justify-between mb-5">
        <p className="text-sm text-gray-500">Create posts for Facebook & Instagram — build one from a car in one click.</p>
        <button data-testid="new-post-btn" onClick={() => { setEditPost(null); setComposer(true); }} className="flex items-center gap-2 px-4 py-2.5 gpm-text-cream text-sm font-semibold" style={{ backgroundColor: "var(--gpm-green)" }}><Plus size={16} /> New post</button>
      </div>

      {posts.length === 0 && <div className="py-16 text-center text-gray-400 border border-dashed border-black/15">No posts yet. Click <b>New post</b> to create your first one.</div>}

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
        {posts.map((p) => {
          const st = STATUS[p.status] || STATUS.draft;
          const m = p.metrics || {};
          return (
            <div key={p.id} data-testid={`post-card-${p.id}`} className="bg-white border border-black/10 flex flex-col">
              {p.images?.[0] ? <img src={imgSrc(p.images[0])} alt="" className="w-full aspect-square object-cover" /> : <div className="w-full aspect-square bg-gray-100 flex items-center justify-center text-gray-300 text-sm">No photo</div>}
              <div className="p-4 flex flex-col flex-1">
                <div className="flex items-center gap-2 mb-2">
                  <span className={`text-[10px] uppercase font-semibold px-2 py-0.5 rounded ${st.c}`}>{st.l}</span>
                  <div className="ml-auto flex gap-1.5">
                    {p.channels?.includes("facebook") && <Facebook size={14} style={{ color: "#1877F2" }} />}
                    {p.channels?.includes("instagram") && <Instagram size={14} style={{ color: "#DD2A7B" }} />}
                  </div>
                </div>
                <p className="text-sm text-gray-700 line-clamp-3 mb-3 whitespace-pre-wrap flex-1">{p.caption}</p>
                {p.status === "scheduled" && p.scheduledAt && <p className="text-xs text-gray-400 mb-2">Scheduled: {new Date(p.scheduledAt).toLocaleString()}</p>}
                {p.status === "published" && p.live && (
                  <div className="flex items-center gap-1 text-xs text-green-700 mb-3 border-t border-black/5 pt-2 font-semibold"><ShieldCheck size={12} /> Live on Meta</div>
                )}
                {p.status === "published" && !p.live && (
                  <div className="flex flex-wrap gap-3 text-xs text-gray-500 mb-3 border-t border-black/5 pt-2">
                    <span className="flex items-center gap-1"><Eye size={12} /> {(m.reach || 0).toLocaleString()}</span>
                    <span className="flex items-center gap-1"><Heart size={12} /> {m.likes || 0}</span>
                    <span className="flex items-center gap-1"><MessageCircle size={12} /> {m.comments || 0}</span>
                    <span className="flex items-center gap-1"><Share2 size={12} /> {m.shares || 0}</span>
                  </div>
                )}
                <div className="flex flex-wrap gap-2 mt-auto">
                  {p.status !== "published" && <button data-testid={`publish-${p.id}`} onClick={() => publish(p.id)} className="flex items-center gap-1 text-xs font-semibold px-2.5 py-1.5 gpm-text-cream" style={{ backgroundColor: "var(--gpm-green)" }}><Send size={12} /> Publish</button>}
                  <button data-testid={`boost-${p.id}`} onClick={() => setBoostPost(p)} className="flex items-center gap-1 text-xs font-semibold px-2.5 py-1.5 border-2" style={{ borderColor: "var(--gpm-gold)", color: "var(--gpm-green)" }}><Rocket size={12} /> Make ad</button>
                  <button data-testid={`edit-${p.id}`} onClick={() => { setEditPost(p); setComposer(true); }} className="flex items-center gap-1 text-xs font-semibold px-2.5 py-1.5 border border-black/20"><Pencil size={12} /></button>
                  <button data-testid={`delete-${p.id}`} onClick={() => remove(p.id)} className="flex items-center gap-1 text-xs font-semibold px-2.5 py-1.5 border border-black/20 text-red-600"><Trash2 size={12} /></button>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {composer && <PostComposer post={editPost} onClose={() => setComposer(false)} onSaved={() => { setComposer(false); refresh(); }} />}
      {boostPost && <AdModal post={boostPost} onClose={() => setBoostPost(null)} onSaved={() => { setBoostPost(null); refresh(); }} />}
    </div>
  );
};

export default SocialPosts;
